import { useCallback, useEffect, useMemo, useRef } from "react";
import type { Patient } from "fhir/r5";
import { oids } from "@/configs/Settings";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import { getPatientCpf } from "../transformers/wooFormatters";
import { ecommerceSupportDocumentsService } from "../services/ecommerceSupportDocumentsService";
import type { DocumentCategory } from "../types";

const EMPTY_CATEGORIES: DocumentCategory[] = [];
const EMPTY_RESULT = {
  categories: EMPTY_CATEGORIES,
  forbidden: false,
};

export interface UsePatientSupportDocumentsResult {
  categories: DocumentCategory[];
  /** Primeira carga sem dados anteriores. */
  loading: boolean;
  /** Recarregando o mesmo paciente — UI pode manter conteúdo com aviso. */
  refreshing: boolean;
  error: boolean;
  forbidden: boolean;
  missingCpf: boolean;
  refetch: () => void;
}

/**
 * Busca documentos de suporte quando `enabled` e o paciente têm CPF.
 */
export function usePatientSupportDocuments(
  patient: Patient | null,
  organizationId: string | undefined,
  enabled: boolean,
): UsePatientSupportDocumentsResult {
  const cpf = patient ? getPatientCpf(patient) : "";
  const missingCpf = !cpf;

  const patientKey = patient?.id
    ? `Patient/${patient.id}`
    : cpf
      ? `Patient/${oids.cpf}-${cpf}`
      : "";

  const generationRef = useRef(0);
  const loadedPatientKeyRef = useRef("");

  const loadTask = useCallback(
    (params: { cpf: string; organizationId?: string; patientKey: string }) =>
      ecommerceSupportDocumentsService.loadSupportDocuments(params),
    [],
  );

  const {
    run: runLoad,
    loading: taskLoading,
    error: taskError,
    data,
    status,
    reset,
  } = useAsyncTask<
    [{ cpf: string; organizationId?: string; patientKey: string }],
    { categories: DocumentCategory[]; forbidden: boolean }
  >({
    task: loadTask,
    initialData: EMPTY_RESULT,
  });

  const categories = data?.categories ?? EMPTY_CATEGORIES;
  const forbidden = data?.forbidden ?? false;

  const fetchDocuments = useCallback(async () => {
    if (!enabled || !cpf) {
      reset();
      loadedPatientKeyRef.current = "";
      return;
    }

    const generation = ++generationRef.current;
    const isSamePatient = loadedPatientKeyRef.current === patientKey;
    if (!isSamePatient) {
      reset();
      loadedPatientKeyRef.current = patientKey;
    }

    try {
      await runLoad({ cpf, organizationId, patientKey });
      if (generation !== generationRef.current) return;
      loadedPatientKeyRef.current = patientKey;
    } catch {
      // erro já capturado pelo useAsyncTask
    }
  }, [cpf, enabled, organizationId, patientKey, reset, runLoad]);

  useEffect(() => {
    void fetchDocuments();
    return () => {
      generationRef.current += 1;
    };
  }, [fetchDocuments]);

  const loading = useMemo(
    () =>
      Boolean(enabled && cpf) &&
      !forbidden &&
      !taskError &&
      categories.length === 0 &&
      (taskLoading || status === "idle"),
    [
      enabled,
      cpf,
      forbidden,
      taskError,
      categories.length,
      taskLoading,
      status,
    ],
  );
  const refreshing = useMemo(
    () => taskLoading && categories.length > 0,
    [taskLoading, categories.length],
  );

  return {
    categories,
    loading,
    refreshing,
    error: Boolean(taskError) && !forbidden,
    forbidden,
    missingCpf,
    refetch: fetchDocuments,
  };
}
